import type React from "react";
import type { ReactNode } from "react";
import { Button } from "../atoms/Button";
import { Heading } from "../atoms/Heading";
import { Input } from "../atoms/Input";
import { Text } from "../atoms/Text";
import { cn } from "../lib/cn";
import { FieldGroup } from "../molecules/FieldGroup";

export interface LoanLookupPageProps {
	searchValue: string;
	onSearchChange: (value: string) => void;
	onSubmit: () => void;
	recentLookups?: string[];
	className?: string;
}

function LoanLookupPage({
	searchValue,
	onSearchChange,
	onSubmit,
	recentLookups = [],
	className,
}: LoanLookupPageProps): ReactNode {
	function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
		if (e.key === "Enter") {
			onSubmit();
		}
	}

	return (
		<div
			className={cn(
				"mx-auto flex w-full max-w-md flex-col gap-6 py-16",
				className,
			)}
		>
			<div className="flex flex-col gap-2">
				<Heading size="2xl">Look up a loan</Heading>
				<Text size="sm" textColor="mutedForeground">
					Enter the loan's reference number to view its verified audit trail.
					You'll find it on your engagement paperwork — for example,
					LN-2024-00123.
				</Text>
			</div>

			<div className="flex flex-col gap-3">
				<FieldGroup id="loan-search" label="Loan reference">
					<Input
						id="loan-search"
						value={searchValue}
						onChange={(e) => onSearchChange(e.target.value)}
						placeholder="e.g. LN-2024-00123"
						onKeyDown={handleKeyDown}
					/>
				</FieldGroup>
				<Button
					onClick={onSubmit}
					className="w-full"
					disabled={!searchValue.trim()}
				>
					Look up
				</Button>
			</div>

			{recentLookups.length > 0 ? (
				<div className="flex flex-col gap-2">
					<Text size="sm" textColor="mutedForeground" weight="medium">
						Recent
					</Text>
					<ul className="flex flex-col gap-1">
						{recentLookups.map((id) => (
							<li key={id}>
								<button
									type="button"
									onClick={() => onSearchChange(id)}
									className="text-sm text-primary underline-offset-4 hover:underline"
								>
									{id}
								</button>
							</li>
						))}
					</ul>
				</div>
			) : (
				<Text size="xs" textColor="mutedForeground">
					Loans you look up will appear here for quick access.
				</Text>
			)}
		</div>
	);
}
LoanLookupPage.displayName = "LoanLookupPage";

export default LoanLookupPage;
export { LoanLookupPage };
